// ./src/contexts/BrandingContext/BrandingContext.tsx
//
// The brand the dashboard shows — A2e #101: the WHITE LABEL of the host it was opened on (`whiteLabelByDomain`), never
// a name written in code. Every user-facing brand name in the app reads `useBranding().brand.name`.
//
//   • It starts from the brand REMEMBERED for this host (utils/appBrand — localStorage), so a returning visit and the
//     installed app open with their brand at once; the read on every load refreshes it.
//   • A FIRST visit has nothing remembered: `ready` stays false (App shows the loader with the generic icon) until the
//     read answers or FIRST_VISIT_WAIT_MS passes — then the brand, or, when nothing could be read, the host's own name.
//   • Only an ANSWER is remembered: a failed read keeps what was there; a host the API says has no white label is
//     forgotten.
// Styles stay local (the build's theme variant); spaceId / themeId / themeTokens are carried on `whiteLabel`.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { brandStyle } from '@/config/branding';
import { BrandingService, DomainBranding } from '@/utils/api/BrandingService';
import { AppBrand, appBrandFrom, brandHostOf, readCachedBranding, writeCachedBranding } from '@/utils/appBrand';
import { setBrandSiteUrl } from '@/utils/eventPublicUrl';

/** How long a first visit (no remembered brand) waits for the white label before showing the app without one. */
export const FIRST_VISIT_WAIT_MS = 3000;

interface BrandingContextValue {
  /** The effective brand for this host. */
  brand: AppBrand;
  /** Raw white-label record for this host (remembered or read), or null. */
  whiteLabel: DomainBranding | null;
  /** True until this load's read has settled. */
  loading: boolean;
  /** False only on a first visit while the brand is still being read — draw the loader, not the app. */
  ready: boolean;
}

/** Host to resolve — `?wlDomain=` stands in for it on a LOCAL host only (utils/appBrand `brandHostOf`). */
const resolveHost = (): string => (typeof window === 'undefined' ? '' : brandHostOf(window.location.hostname, window.location.search));

/** localStorage, or null where the browser blocks it (the accessor itself can throw). */
const browserStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

const BrandingContext = createContext<BrandingContextValue>({
  brand: appBrandFrom(null, '', brandStyle.themeColor),
  whiteLabel: null,
  loading: false,
  ready: true,
});

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [host] = useState(resolveHost);
  const [remembered] = useState(() => readCachedBranding(browserStorage(), host));
  const [whiteLabel, setWhiteLabel] = useState<DomainBranding | null>(remembered);
  const [settled, setSettled] = useState(false);
  const [waitedOut, setWaitedOut] = useState(false);

  useEffect(() => {
    let active = true;
    const timer = remembered ? null : window.setTimeout(() => setWaitedOut(true), FIRST_VISIT_WAIT_MS);

    BrandingService.resolveByDomain(host).then((answer) => {
      if (!active) {
        return;
      }

      if (timer !== null) {
        window.clearTimeout(timer);
      }
      if (answer.answered) {
        setWhiteLabel(answer.branding);
        writeCachedBranding(browserStorage(), host, answer.branding);
      }
      setSettled(true);
    });

    return () => {
      active = false;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [host, remembered]);

  // The brand changes only with the white label — not with the flags below (the head re-applies on every new brand).
  const brand = useMemo(() => appBrandFrom(whiteLabel, host, brandStyle.themeColor), [whiteLabel, host]);
  // Every share / QR / public link the app builds outside React (utils/eventPublicUrl) — set during render, so the
  // first render that uses it already has it.
  setBrandSiteUrl(brand.siteUrl);
  const value = useMemo<BrandingContextValue>(
    () => ({
      brand,
      whiteLabel,
      loading: !settled,
      ready: !!remembered || settled || waitedOut,
    }),
    [brand, whiteLabel, settled, remembered, waitedOut],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): BrandingContextValue => useContext(BrandingContext);

/**
 * The brand's default phone country (ISO 3166-1 alpha-2, uppercased) — the
 * default parse region for the shared PhoneField, falling back to 'US'. Mirrors
 * the server-side `space.default_country`.
 */
export const useDefaultPhoneCountry = (): string => {
  const cc = useBranding().whiteLabel?.defaultCountry;

  return (cc || 'US').toUpperCase();
};
